import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Collapse,
  FormControlLabel,
  Tooltip,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {

  ModeEdit as ModeEditIcon,
  DeleteForever as DeleteForeverIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LastPage as LastPageIcon,
  FirstPage as FirstPageIcon,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  MoreVert as MoreVertIcon,
  DirectionsBoat as DirectionsBoatIcon,
  Add as AddIcon,
  LocalShipping as LocalShippingIcon,
  Layers as LayersIcon,
  CallSplit as CallSplitIcon,
} from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../components/Loader";
import PropTypes from "prop-types";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { resetState } from "../../../../slices/vehicleInfoSlice";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const headCells = [
  { id: "expand", label: "", disablePadding: false },
  { id: "id", label: "ID", disablePadding: false },
  { id: "entryDate", label: "Entry Date", disablePadding: false },
  { id: "number", label: "Booking Number", disablePadding: false },
  { id: "bookingStatus", label: "Status", disablePadding: false },
  { id: "vesselName", label: "Vessel Name", disablePadding: false },
  { id: "voyageNumber", label: "Voyage Number", disablePadding: false },
  { id: "destination", label: "Destination", disablePadding: false },
  { id: "shippingLine", label: "Shipping Line", disablePadding: false },
  { id: "broker", label: "Broker", disablePadding: false },
  { id: "portLoading", label: "Port Loading", disablePadding: false },
  { id: "pickupTerminal", label: "Pickup Terminal", disablePadding: false },
  { id: "returnTerminal", label: "Return Terminal", disablePadding: false },
  { id: "earliestReceivingDate", label: "Earliest Receiving Date", disablePadding: false },
  { id: "latestReturnDate", label: "Latest Return Date", disablePadding: false },
  { id: "cutoffDate", label: "DOC Cutoff Date", disablePadding: false },
  { id: "estimatedSailingDate", label: "Estimated Sailing Date", disablePadding: false },
  { id: "estimatedArrivalDate", label: "Estimated Arrival Date", disablePadding: false },
  { id: "note", label: "Note", disablePadding: false },
  { id: "actions", label: "Actions", disablePadding: false },
];

function EnhancedTableHead({ order, orderBy, onRequestSort, isSelectedBooking }) {
  const createSortHandler = (property) => (event) => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          // Hide actions column when selecting a booking
          if (isSelectedBooking && headCell.id === "actions") return null;

          return (
            <TableCell
              key={headCell.id}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
              variant="head"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {headCell.id !== "expand" && headCell.id !== "actions" ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {t(headCell.label).toUpperCase()}
                  {orderBy === headCell.id && (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </Box>
                  )}
                </TableSortLabel>
              ) : (
                t(headCell.label)
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const CollapseView = ({ row }) => {
  const borderColor = "#bdbdbd"; // لون رمادي غامق وواضح

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent: "start",
        m: 2,
        backgroundColor: "colorA.main",
        border: `1.5px solid ${borderColor}`,
        borderRadius: 2,
        overflow: "hidden",
        width: "70%"
      }}
    >
      {/* العمود الأول */}
      <Box sx={{ display: "flex", width: "50%", m: 2 }}>
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <Typography variant="button" sx={{ textAlign: "center", width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>INFO</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>STATUS</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>ESTIMATED SAILING DATE</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>ESTIMATED ARRIVAL DATE</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>EARLIEST RECEIVING DATE</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27 }}>LATEST RETURN DATE</Typography>
        </Box>
        <Box sx={{ width: "1.5px", backgroundColor: borderColor, mx: 1 }} />

        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "start", gap: 0 }}>
          <Typography variant="button" sx={{ textAlign: "center", width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>DETAILS</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row?.bookingStatus?.name || "***"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row.estimatedSailingDate || "***"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row.estimatedArrivalDate || "***"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row.earliestReceivingDate || "***"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27 }}>{row.latestReturnDate || "***"}</Typography>
        </Box>
      </Box>
      <Box sx={{ width: "1.5px", backgroundColor: borderColor, mt: 4, mb: 4 }} />
      <Box sx={{ width: "1.5px", backgroundColor: borderColor, mt: 4, mb: 4 }} />

      {/* العمود الثاني */}
      <Box sx={{ display: "flex", width: "35%", m: 2 }}>
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <Typography variant="button" sx={{ textAlign: "center", width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>INFO</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>BOOKING NUMBER</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>DOC CUTOFF DATE</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>RECEIVING DATE</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>DOC RECEIVING DATE</Typography>

          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27 }}>BOOKING NOTE</Typography>
        </Box>
        <Box sx={{ width: "1.5px", backgroundColor: borderColor, mx: 1 }} />
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "start", gap: 0 }}>
          <Typography variant="button" sx={{ textAlign: "center", width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>DETAILS</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row?.number || "N/A"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row.cutoffDate || "N/A"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row.containerGateInDate || "N/A"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27, borderBottom: `1.5px solid ${borderColor}` }}>{row.shippingInstructionsSent || "N/A"}</Typography>
          <Typography variant="body2" sx={{ width: "100%", color: "secondary.main", minHeight: 27 }}>{row?.note || "N/A"}</Typography>
        </Box>
      </Box>
    </Box>
  );
};



const BookingTable = ({ showCreateShipment = true, handleSelectBooking , handlingFetchData}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchData = useSelector((state) => state.manage);
  const userReducer = useSelector((state) => state.user);

  const [isAbleDelete, setAbleDelete] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [openArray, setOpenArray] = useState({ Name: [] });
  const [dense, setDense] = useState(true);
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, row: null });
  const [shipmentTypeMenu, setShipmentTypeMenu] = useState({ anchorEl: null, row: null });

  const data = fetchData.object || {};
  const content = data.content || [];

  // URL query parameters and their setters
  const page = Number(searchParams.get("PageNumber")) || 0;
  const rowsPerPage = Number(searchParams.get("PageSize")) || 5;
  const order = searchParams.get("SortOrder") || "desc";
  const orderBy = searchParams.get("SortBy") || "id";

  const setPage = (value) => {
    searchParams.set("PageNumber", value);
    setSearchParams(searchParams);
  };
  const setRowsPerPage = (value) => {
    searchParams.set("PageSize", value);
    setSearchParams(searchParams);
  };
  const setOrder = (value) => {
    searchParams.set("SortOrder", value);
    setSearchParams(searchParams);
  };
  const setOrderBy = (value) => {
    searchParams.set("SortBy", value);
    setSearchParams(searchParams);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleChangeDense = (event) => setDense(event.target.checked);

  const handelEdit = (id) => {
    dispatch(resetState());
    navigate(`/addBooking?id=${id}&backLink=` + searchParams.toString().replaceAll("&", "*"));
  };

  const handelOpenDeleteModal = (item) => {
    setSelectedDeleteItem(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const openActionMenu = (e, row) => {
    e.stopPropagation();
    setActionMenu({ anchorEl: e.currentTarget, row });
  };
  const closeActionMenu = () => setActionMenu({ anchorEl: null, row: null });
  const openShipmentTypeMenu = (e, row) => {
    e.stopPropagation();
    setShipmentTypeMenu({ anchorEl: e.currentTarget, row });
  };
  const closeShipmentTypeMenu = () => setShipmentTypeMenu({ anchorEl: null, row: null });

  const handleViewShipment = (row) => {
    if (row?.id) {
      navigate(`/shipments?BookingId=${row.id}`);
    }
  };

  const handleCreateShipment = (row, shipmentMode) => {
    closeShipmentTypeMenu();
    navigate(`/addShipment?shipmentMode=${encodeURIComponent(shipmentMode)}`, { state: { booking: row } });
  };

  const openCollapse = (index) => {
    const lastArray = [...openArray.Name];
    const idx = lastArray.indexOf(index);
    if (idx > -1) {
      lastArray.splice(idx, 1);
    } else {
      lastArray.push(index);
    }
    setOpenArray({ Name: lastArray });
  };
  const checkIOpen = (index) => openArray.Name.indexOf(index) > -1;

  useEffect(() => {
    userReducer?.userInfo?.user?.roles.some((role) => {
      if (
        role.name === "ROLE_SUPER_EDITOR" ||
        role.name === "ROLE_SUPER" ||
        role.name === "ROLE_ADMIN"
      ) {
        setAbleDelete(true);
        return true;
      }
      return false;
    });
  }, [userReducer]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.totalElements) : 0;

  if (
    data.loading === "pending" ||
    data.loading === "idle" ||
    userReducer.loading === true
  ) {
    return <Loader />;
  }

  return (
    <Box sx={{ width: "100%", bgcolor: '#F7F8FA', p: { xs: 0, md: 2 }, borderRadius: 3 }}>
      <Paper sx={{ width: "100%", mb: 2, borderRadius: 3, boxShadow: '0 2px 12px 0 rgba(11, 60, 93, 0.10)' }}>
        <TableContainer sx={{ borderRadius: 3 }}>
          <Table
            sx={{ minWidth: 750, background: '#fff', borderRadius: 3 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              isSelectedBooking={handleSelectBooking ? true : false}
            />
            <TableBody>
              {content.map((row, index) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#F7F8FA' : '#fff',
                      '&:hover': { backgroundColor: '#E9ECEF' },
                      transition: 'background 0.2s',
                    }}
                    tabIndex={-1}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {handleSelectBooking && (
                          <Checkbox
                            onChange={() => handleSelectBooking(row)}
                          />
                        )}
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => openCollapse(index)}
                        >
                          {checkIOpen(index) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.id}</Typography>
                        {row?.bookingStatus?.id === 1 && showCreateShipment && (
                          <Tooltip title={t('Create Shipment')} arrow>
                            <IconButton
                              size="small"
                              onClick={(event) => openShipmentTypeMenu(event, row)}
                              aria-label={t('Create Shipment')}
                              sx={{ position: 'relative', width: 28, height: 28, p: 0.5, bgcolor: '#E6F2FE', border: '1.5px solid #0D47A1', '&:hover': { bgcolor: '#D7E8FD' } }}
                            >
                              <DirectionsBoatIcon sx={{ fontSize: 17, color: '#0D47A1' }} />
                              <AddIcon sx={{ position: 'absolute', right: -4, bottom: -4, fontSize: 14, color: '#2E7D32', bgcolor: '#fff', borderRadius: '50%' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{row.entryDate}</TableCell>
                    <TableCell>{row.number}</TableCell>
                    <TableCell>{row?.bookingStatus?.name}</TableCell>
                    <TableCell>{row.vesselName}</TableCell>
                    <TableCell>{row.voyageNumber}</TableCell>
                    <TableCell>{row?.destination?.name}</TableCell>
                    <TableCell>{row?.shippingLine?.name}</TableCell>
                    <TableCell>{row?.broker?.name ? row.broker.name : 'N/A'}</TableCell>
                    <TableCell>{row?.portLoading?.name}</TableCell>
                    <TableCell>{row?.pickupTerminal?.name}</TableCell>
                    <TableCell>{row?.returnTerminal?.name}</TableCell>

                    <TableCell>{row.earliestReceivingDate}</TableCell>
                    <TableCell>{row.latestReturnDate}</TableCell>
                    <TableCell>{row.cutoffDate}</TableCell>
                    <TableCell>{row.estimatedSailingDate}</TableCell>
                    <TableCell>{row.estimatedArrivalDate}</TableCell>
                    <TableCell>{row.note}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={t('More')} arrow>
                          <IconButton size="small" onClick={(e) => openActionMenu(e, row)} sx={{ bgcolor: '#F5F7F9', color: '#455A64', width: 28, height: 28 }}>
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headCells.length}>
                      <Collapse in={checkIOpen(index)} timeout="auto" unmountOnExit>
                        <CollapseView row={row} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={headCells.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          ActionsComponent={TablePaginationActions}
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            boxShadow: '0 1px 4px 0 rgba(11, 60, 93, 0.08)',
            mt: 1
          }}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label={t("Densepadding")}
        sx={{ mt: 1, ml: 1 }}
      />
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={()=>{handleCloseDeleteModal(); handlingFetchData();}}
          deleteItem={selectedDeleteItem}
        />
      )}

      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(!handleSelectBooking || actionMenu.row?.status?.id === 1) && (
          <MenuItem onClick={() => { if (actionMenu.row) { handelEdit(actionMenu.row.id); } closeActionMenu(); }}>
            <ListItemIcon><ModeEditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('Edit')}</ListItemText>
          </MenuItem>
        )}



        {actionMenu.row?.bookingStatus.id > 2 ? (
          <>

            <MenuItem onClick={() => { if (actionMenu.row) { handleViewShipment(actionMenu.row); } closeActionMenu(); }}>
              <ListItemIcon><DirectionsBoatIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('View Shipment')}</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => { if (actionMenu.row) { navigate(`/vehicles?MixSearch=${actionMenu.row.number}`); } closeActionMenu(); }}>
              <ListItemIcon><DirectionsCarIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('View Vehicles')}</ListItemText>
            </MenuItem>


          </>

        ) : null}

        {(isAbleDelete && !handleSelectBooking && actionMenu.row?.bookingStatus?.id === 1 && showCreateShipment) && (
          <MenuItem onClick={() => { if (actionMenu.row) { handelOpenDeleteModal(actionMenu.row); } closeActionMenu(); }}>
            <ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: 'error.main' }}>{t('Delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={shipmentTypeMenu.anchorEl}
        open={Boolean(shipmentTypeMenu.anchorEl)}
        onClose={closeShipmentTypeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: 260,
            p: 1,
            boxShadow: '0 14px 36px rgba(11, 60, 93, 0.18)',
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, mb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0B3C5D' }}>
            {t('Choose Shipment Type')}
          </Typography>
        </Box>
        <MenuItem
          onClick={() => handleCreateShipment(shipmentTypeMenu.row, 'FCL')}
          sx={{ borderRadius: 2, gap: 1.5, px: 1.5, py: 1.25, mb: 0.75, bgcolor: '#E8F1FF', '&:hover': { bgcolor: '#D3E6FF' } }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#C9DEFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LocalShippingIcon sx={{ color: '#1D4DB5', fontSize: 20 }} />
            </Box>
          </ListItemIcon>
          <ListItemText primary={t('FCL')} secondary={t('Full Container Load')} primaryTypographyProps={{ fontWeight: 800, fontSize: 14 }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleCreateShipment(shipmentTypeMenu.row, 'LCL')}
          sx={{ borderRadius: 2, gap: 1.5, px: 1.5, py: 1.25, mb: 0.75, bgcolor: '#FFF4E6', '&:hover': { bgcolor: '#FFE7CC' } }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#F6D49F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LayersIcon sx={{ color: '#B56B00', fontSize: 20 }} />
            </Box>
          </ListItemIcon>
          <ListItemText primary={t('LCL')} secondary={t('Less than Container Load')} primaryTypographyProps={{ fontWeight: 800, fontSize: 14 }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleCreateShipment(shipmentTypeMenu.row, 'SPL')}
          sx={{ borderRadius: 2, gap: 1.5, px: 1.5, py: 1.25, bgcolor: '#EAF9ED', '&:hover': { bgcolor: '#D7F2D4' } }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#B8E6B0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CallSplitIcon sx={{ color: '#207A22', fontSize: 20 }} />
            </Box>
          </ListItemIcon>
          <ListItemText primary={t('SPL')} secondary={t('Split container loaded')} primaryTypographyProps={{ fontWeight: 800, fontSize: 14 }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

BookingTable.displayName = "BookingTable";
BookingTable.propTypes = {
  handlingFetchData: PropTypes.func.isRequired,
  showCreateShipment: PropTypes.bool,
};

export default BookingTable;
